import { useTranslation } from 'react-i18next';
import type { SiteId, TaskId } from '@interfaces/IScheduler';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { ProgressMeter } from '@components/ui/ProgressMeter/ProgressMeter';
import { CheckCircleFilledIcon } from '@utils/icons';
import { useChecklistTicks } from '../../hooks/useChecklistTicks';
import { ChecklistCaptureForm } from './ChecklistCaptureForm';
import { ChecklistEvidence } from './ChecklistEvidence';
import { ChecklistEvidenceTags } from './ChecklistEvidenceTags';
import { useSchedulerCapabilities } from '../../context/SchedulerCapabilities';
import styles from './ChecklistCard.module.css';

// ---------------------------------------------------------------------------
// CHECKLIST — the task's checklist as the promise's definition of done
// (docs/specs/2026-09-28-task-checklist-design.md, decision 10 / goal G11).
// It sits directly under the promise block: what the commit agreed, ticked
// off as the work is inspected, each tick carrying the evidence its item asks
// for. The meter is the same count the Mark done step waits on.
//
// What a tick does is `useChecklistTicks`'s; this card only shows the list
// and hands the checkbox and the capture form their callbacks. Before the
// commit there is nothing to tick against — the list shows what the promise
// will ask for, disabled.
// ---------------------------------------------------------------------------

interface IChecklistCardProps {
  taskId: TaskId;
  siteId: SiteId;
  /** Loaded schedule — enables the tick PUT and the photo upload. */
  scheduleNumber?: string;
  /** The site's zone; undefined while it resolves (no times, no 黒板 date). */
  tz: string | undefined;
}

export function ChecklistCard({ taskId, siteId, scheduleNumber, tz }: IChecklistCardProps) {
  const { t } = useTranslation();
  const ticks = useChecklistTicks(siteId, taskId, scheduleNumber, tz);
  // Without `tasks.execute` the ticks are read as plain marks, no checkbox.
  const { canExecute } = useSchedulerCapabilities();
  const { task, items, tickable, capturingId } = ticks;
  if (!task) return null;
  const { done, total } = ticks.progress;
  const complete = total > 0 && done === total;

  return (
    <section className={styles.card} data-testid="checklist-card">
      <header className={styles.header}>
        <h3 className={styles.title}>{t('scheduler.panel.checklist.title')}</h3>
        {total > 0 && (
          <span className={complete ? styles.countDone : done < total ? styles.countOpen : styles.count} data-testid="checklist-count">
            {complete && <CheckCircleFilledIcon size={12} />}
            {t(complete ? 'scheduler.panel.checklist.allDone' : 'scheduler.panel.checklist.progress', { done, total })}
          </span>
        )}
      </header>
      {total > 0 && <ProgressMeter value={(done / total) * 100} />}
      {!task.committed && <p className={styles.note}>{t('scheduler.panel.checklist.uncommitted')}</p>}
      {ticks.refused && <p className={styles.error} role="alert">{t('scheduler.panel.checklist.tickRefused')}</p>}

      <ul className={styles.list}>
        {items.map((item) =>
          item.kind === 'description' ? (
            <li key={item.id} className={styles.guidance}>{item.content}</li>
          ) : (
            <li key={item.id} className={styles.item}>
              <label className={[styles.row, canExecute && tickable ? '' : styles.rowDisabled, item.completion?.checked ? styles.rowChecked : ''].join(' ')}>
                {canExecute ? (
                  <Checkbox
                    checked={!!item.completion?.checked}
                    disabled={!tickable}
                    ariaLabel={item.content}
                    onCheckedChange={(next) => ticks.toggle(item, next)}
                  />
                ) : (
                  // No `tasks.execute`: the tick is read, not offered.
                  <span className={styles.readMark} aria-hidden>
                    {item.completion?.checked && <CheckCircleFilledIcon size={14} />}
                  </span>
                )}
                <span className={styles.rowText}>
                  <span className={styles.content}>{item.content}</span>
                  {!item.completion?.checked && <ChecklistEvidenceTags item={item} />}
                </span>
              </label>
              {item.completion?.checked && <ChecklistEvidence completion={item.completion} tz={tz} />}
              {capturingId === item.id && (
                <ChecklistCaptureForm
                  item={item}
                  upload={ticks.evidenceTarget(item.id)}
                  blackboard={ticks.blackboard}
                  checkedByName={ticks.checkedByName}
                  onCancel={ticks.cancelCapture}
                  onCommit={(completion) => ticks.commitCapture(item.id, completion)}
                />
              )}
            </li>
          ),
        )}
      </ul>
    </section>
  );
}
